/**
 * Author: Claude Code
 * Generation: Google Identity Services utility for FedCM compatibility
 * Version: v3.2.229
 * Modified: 2025-11-05 (Refactor to @react-oauth/google pattern, remove deprecated API)
 */

/** JWT token decoded structure from Google */
export interface GoogleTokenPayload {
  sub: string; // User ID
  email: string;
  email_verified: boolean;
  name: string;
  picture: string;
  aud: string; // Client ID
  iat: number; // Issued at
  exp: number; // Expiration
  iss: string; // Issuer
}

/** User info extracted from Google token */
export interface GoogleUserInfo {
  id: string;
  email: string;
  name: string;
  picture: string;
  emailVerified: boolean;
}

/** Decode JWT token and extract payload (client-side basic parsing) */
function decodeJWT(token: string): GoogleTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[GIS] Invalid JWT format');
      return null;
    }

    // Decode payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (err: any) {
    console.error('[GIS] Failed to decode token:', err?.message);
    return null;
  }
}

/** Extract user info from Google credential token */
export function extractUserInfo(credentialToken: string): GoogleUserInfo | null {
  const payload = decodeJWT(credentialToken);
  if (!payload) return null;

  return {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
    picture: payload.picture,
    emailVerified: payload.email_verified
  };
}

/** Validate token expiration */
export function isTokenExpired(credentialToken: string): boolean {
  const payload = decodeJWT(credentialToken);
  if (!payload) return true;

  const now = Math.floor(Date.now() / 1000);
  return now >= payload.exp;
}

/** Log credential reception (for debugging FedCM flow) */
export function logCredentialReceived(credentialToken: string): void {
  const userInfo = extractUserInfo(credentialToken);
  if (userInfo) {
    console.debug('[GIS] ✅ Credential received for:', userInfo.email);
  } else {
    console.error('[GIS] Failed to extract user info from credential');
  }
}

